import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LayoutDashboard, Bike, Battery, Users, Map, Wrench, AlertTriangle, Mail, Shield, FileText, RefreshCw, LogOut, Menu, X, Building2, UserCog, Download, DollarSign, Package, Sparkles, Search, Link as LinkIcon, CheckCircle, Upload, AlertCircle, Settings, ChevronDown, ChevronRight, ShieldCheck, Wallet, UserPlus, Bell, User, Loader2, Bot, Smartphone, MessageSquare, BarChart3 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Toaster } from './ui/sonner';
import AIChatbot from './AIChatbot';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from 'react-i18next';
import useDeviceDetection from '../hooks/useDeviceDetection';
import BottomNavigation from './responsive/BottomNavigation';
import { userCan } from '../utils/permissionUtils';
import '../styles/responsive.css';

const Layout = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet, isDesktop } = useDeviceDetection();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [layoutReady, setLayoutReady] = useState(false);
  const [chatButtonPos, setChatButtonPos] = useState(() => {
    const saved = localStorage.getItem('chatButtonPosition');
    return saved ? JSON.parse(saved) : { bottom: 24, right: 24 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Ensure layout is ready after mount (fixes iOS Safari blank screen)
  useEffect(() => {
    // Small delay to ensure device detection has completed
    const timer = setTimeout(() => {
      setLayoutReady(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Close sidebar when switching to desktop (use a ref to avoid cascading renders)
  useEffect(() => {
    if (isDesktop && sidebarOpen) {
      // Defer setState to avoid cascading renders
      requestAnimationFrame(() => {
        setSidebarOpen(false);
      });
    }
  }, [isDesktop, sidebarOpen]);

  // Helper function to check if user has permission to view a module
  const canViewModule = (moduleKey) => {
    if (user?.role === 'Super Admin' || user?.role === 'Admin') {
      return true;
    }
    if (user?.permissions) {
      const modulePerms = user.permissions[moduleKey];
      if (modulePerms && modulePerms.view === true) {
        return true;
      }
    }
    return false;
  };

  // Map routes to permission module keys
  const routeToModule = {
    '/': 'dashboard',
    '/profile': null,
    '/motorcycles': 'motorcycles',
    '/batteries': 'batteries',
    '/battery-swaps': 'batterySwaps',
    '/riders': 'riders',
    '/lessees': 'lessees',
    '/maintenance': 'maintenance',
    '/fines': 'fines',
    '/inbox': 'inbox',
    '/salik': 'salik',
    '/talent': 'talent',
    '/tap': 'tap',
    '/hiring': 'hiring',
    '/spares': 'spares',
    '/inventory': 'inventory',
    '/insurance': 'insurance',
    '/invoices': 'invoices',
    '/reports': 'reports',
    '/searchable-dropdown-demo': 'search',
    '/approvals': 'approvals',
    '/analysis': 'analysis',
  };

  // Regular menu items (non-admin)
  const allMenuItems = [
    { path: '/profile', icon: Users, label: t('nav.profile') || 'My Profile' },
    { path: '/', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/motorcycles', icon: Bike, label: t('nav.motorcycles') },
    { path: '/batteries', icon: Battery, label: t('nav.batteries') },
    { path: '/battery-swaps', icon: RefreshCw, label: t('nav.batterySwaps') },
    { path: '/riders', icon: Users, label: t('nav.riders') },
    { path: '/lessees', icon: Building2, label: t('nav.lessees') },
    { path: '/maintenance', icon: Wrench, label: t('nav.maintenance') },
    { path: '/fines', icon: AlertTriangle, label: t('nav.fines') },
    { path: '/inbox', icon: Mail, label: 'Inbox' },
    { path: '/salik', icon: DollarSign, label: t('nav.salik') },
    { path: '/talent', icon: Wallet, label: 'Talent (PMS)' },
    { path: '/tap', icon: UserPlus, label: 'TAP' },
    { path: '/hiring', icon: Users, label: 'Hiring' },
    { path: '/spares', icon: Package, label: t('nav.spares') },
    { path: '/inventory', icon: Smartphone, label: 'Inventory' },
    { path: '/insurance', icon: Shield, label: t('nav.insurance') },
    { path: '/invoices', icon: FileText, label: 'Accounts' },
    { path: '/reports', icon: Download, label: t('nav.reports') },
    { path: '/analysis', icon: BarChart3, label: 'Analysis' },
    { path: '/searchable-dropdown-demo', icon: Search, label: 'Search' },
  ];

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter(item => {
    const moduleKey = routeToModule[item.path];
    if (moduleKey === null) return true;
    return canViewModule(moduleKey);
  });

  // Admin-only menu items - filtered based on permissions
  const allAdminMenuItems = [
    { path: '/approvals', icon: CheckCircle, label: 'Approvals', moduleKey: 'approvals' },
    { path: '/talent?tab=admin', icon: Settings, label: 'PMS Admin', moduleKey: 'adminTools', subPage: 'pmsAdmin' },
    { path: '/integrations', icon: LinkIcon, label: 'Integrate', moduleKey: 'adminTools', subPage: 'integrations' },
    { path: '/whatsapp', icon: MessageSquare, label: 'WhatsApp', moduleKey: 'adminTools', subPage: 'whatsapp' },
    { path: '/users', icon: UserCog, label: t('nav.users'), moduleKey: 'users' },
    { path: '/settings', icon: Settings, label: 'Settings', moduleKey: 'users', alsoAllow: ['adminTools', 'settings'] },
  ];
  
  // Filter admin menu items based on permissions
  // Users with specific module permissions can see those items
  // Super Admin/Admin can see all items
  const adminMenuItems = allAdminMenuItems.filter(item => {
    if (item.subPage) return userCan(user, item.moduleKey, 'view', item.subPage);
    if (item.alsoAllow && userCan(user, item.alsoAllow[0], 'view', item.alsoAllow[1])) return true;
    return canViewModule(item.moduleKey);
  });

  const currentFullPath = location.pathname + location.search;
  const isAdminPath = adminMenuItems.some(item => 
    currentFullPath === item.path || location.pathname === item.path
  );
  
  if (isAdminPath && !adminExpanded) {
    setAdminExpanded(true);
  }

  const handleDragStart = (e) => {
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleDrag = (e) => {
    if (!isDragging || e.clientX === 0 || e.clientY === 0) return;
    const newRight = window.innerWidth - e.clientX - dragOffset.x;
    const newBottom = window.innerHeight - e.clientY - dragOffset.y;
    setChatButtonPos({
      bottom: Math.max(10, Math.min(window.innerHeight - 100, newBottom)),
      right: Math.max(10, Math.min(window.innerWidth - 100, newRight))
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    localStorage.setItem('chatButtonPosition', JSON.stringify(chatButtonPos));
  };

  // Calculate chat button position based on device
  const getChatButtonPosition = () => {
    if (isMobile) {
      return { bottom: 80, right: 16 }; // Above bottom nav
    }
    if (isTablet) {
      return { bottom: 80, right: 16 }; // Above bottom nav on tablet too
    }
    return chatButtonPos;
  };

  // Show loading state while layout is being prepared (fixes iOS Safari blank screen)
  if (!layoutReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50" style={{ minHeight: '-webkit-fill-available' }}>
        <div className="flex flex-col items-center gap-4">
          <img 
            src="/customer-assets/Ezy_Mobility_Logo_New.png" 
            alt="Ezy Mobility" 
            className="h-16 w-auto animate-pulse"
          />
          <Loader2 className="w-6 h-6 animate-spin text-lime-600" />
          <p className="text-xs text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Debug: Log device detection (can be removed later)
  if (typeof window !== 'undefined' && window.location.search.includes('debug=1')) {
    console.log('Device Detection:', { isMobile, isTablet, isDesktop, layoutReady });
  }

  // ==================== MOBILE/TABLET LAYOUT ====================
  if (isMobile || isTablet) {
    // Use safe rendering for iOS Safari - avoid dynamic height calculations
    const safeUserInitial = user && user.name && typeof user.name === 'string' ? user.name.charAt(0) : 'U';
    
    return (
      <div 
        className="mobile-layout-container"
        style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#f8fafc',
          overflow: 'hidden',
        }}
      >
        <Toaster position="top-center" richColors />
        
        {/* Mobile Header - Fixed position with safe rendering */}
        <header 
          className="mobile-header"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '70px',
            backgroundColor: 'white',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            zIndex: 1000,
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
          }}
        >
          <Link to="/">
            <img 
              src="/customer-assets/Ezy_Mobility_Logo_New.png" 
              alt="Ezy Mobility" 
              style={{ height: '50px', width: 'auto' }}
            />
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Link to="/inbox">
              <Bell style={{ width: '24px', height: '24px', color: '#64748b' }} />
            </Link>
            <Link to="/profile">
              <div 
                style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%', 
                  backgroundColor: '#84cc16',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                }}
              >
                {safeUserInitial}
              </div>
            </Link>
          </div>
        </header>

        {/* Main Content - Scrollable area with explicit dimensions */}
        <main 
          className="mobile-main-content"
          style={{ 
            position: 'absolute',
            top: '70px',
            left: 0,
            right: 0,
            bottom: '64px',
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '12px',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Outlet />
        </main>

        {/* Bottom Navigation */}
        <BottomNavigation user={user} menuItems={menuItems} adminMenuItems={adminMenuItems} onLogout={onLogout} />

        {/* AI Chatbot */}
        {!chatOpen && !chatMinimized && (
          <div
            style={{
              position: 'fixed',
              bottom: `${getChatButtonPosition().bottom}px`,
              right: `${getChatButtonPosition().right}px`,
              zIndex: 999,
            }}
          >
            <Button
              onClick={() => setChatOpen(true)}
              className="h-12 w-12 rounded-full bg-gradient-to-r from-lime-500 to-green-600 hover:from-lime-600 hover:to-green-700 shadow-lg"
            >
              <Sparkles className="w-5 h-5 text-white" />
            </Button>
          </div>
        )}

        {(chatOpen || chatMinimized) && (
          <AIChatbot
            isOpen={chatOpen}
            isMinimized={chatMinimized}
            onClose={() => { setChatOpen(false); setChatMinimized(false); }}
            onMinimize={() => { setChatOpen(false); setChatMinimized(true); }}
            onMaximize={() => { setChatOpen(true); setChatMinimized(false); }}
            user={user}
          />
        )}
      </div>
    );
  }

  // ==================== DESKTOP LAYOUT (Original) ====================
  return (
    <div className="flex h-screen overflow-hidden">
      <Toaster position="top-right" richColors />
      
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-3 justify-center">
              <img 
                src="/customer-assets/Ezy_Mobility_Logo_New.png" 
                alt="Ezy Mobility" 
                className="h-32 w-auto opacity-75 mix-blend-multiply"
              />
            </div>
            <p className="text-sm font-medium text-slate-700 mt-2 text-center">
              {user?.name ? user.name.split(' ')[0] : user?.email?.split('@')[0] || 'User'}
            </p>
          </div>

          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isOnPmsAdmin = location.pathname === '/talent' && location.search === '?tab=admin';
              const isActive = location.pathname === item.path && !(item.path === '/talent' && isOnPmsAdmin);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={(e) => {
                    setSidebarOpen(false);
                    // If already on same path but with query params, force navigate to clear them
                    if (location.pathname === item.path && location.search) {
                      e.preventDefault();
                      navigate(item.path, { replace: true });
                    }
                  }}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-lime-500 to-green-600 text-white shadow-md'
                      : 'text-slate-600 hover:bg-green-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
            
            {/* Admin Section - Collapsible - Show if user has any admin menu items */}
            {adminMenuItems.length > 0 && (
              <div className="pt-2">
                <button
                  onClick={() => setAdminExpanded(!adminExpanded)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                    isAdminPath
                      ? 'bg-red-50 text-red-700'
                      : 'text-slate-600 hover:bg-red-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5" />
                    <span>Admin</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-red-500 text-white text-xs">{user?.role || 'Checker'}</Badge>
                    {adminExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </div>
                </button>
                
                {adminExpanded && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-red-200 pl-2">
                    {adminMenuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = currentFullPath === item.path || 
                        (item.path.includes('?') && currentFullPath.startsWith(item.path));
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                          className={`flex items-center gap-3 px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                            isActive
                              ? 'bg-red-500 text-white shadow-md'
                              : 'text-slate-600 hover:bg-red-50'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-lime-500 to-green-600 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                {user?.profile_photo ? (
                  <img src={user.profile_photo} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  user?.name?.charAt(0)
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <LanguageSelector />
            <Button
              onClick={onLogout}
              variant="outline"
              className="w-full justify-start gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {t('common.logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header - Only on small desktop screens */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-slate-200">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
          <img 
            src="/customer-assets/Ezy_Mobility_Logo_New.png" 
            alt="Ezy Mobility" 
            className="h-16 w-auto opacity-75 mix-blend-multiply"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { setChatOpen(true); setChatMinimized(false); }}
            className="hover:bg-lime-50 p-2"
          >
            <img 
              src="/customer-assets/Ezy_Mobility_Logo_New.png" 
              alt="EZY" 
              className="w-12 h-12 object-contain"
            />
          </Button>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gradient-to-br from-white to-green-50">
          <Outlet />
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* AI Chatbot Floating Button - Desktop */}
      {!chatOpen && (
        <div 
          className="fixed z-50 cursor-move"
          style={{ 
            bottom: `${chatButtonPos.bottom}px`, 
            right: `${chatButtonPos.right}px`,
            touchAction: 'none'
          }}
          draggable
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          onTouchStart={(e) => {
            const touch = e.touches[0];
            handleDragStart({ clientX: touch.clientX, clientY: touch.clientY, currentTarget: e.currentTarget });
          }}
          onTouchMove={(e) => {
            const touch = e.touches[0];
            handleDrag({ clientX: touch.clientX, clientY: touch.clientY });
          }}
          onTouchEnd={handleDragEnd}
        >
          <Button
            onClick={(e) => { 
              e.stopPropagation(); 
              setChatOpen(true); 
              setChatMinimized(false); 
            }}
            className="bg-white hover:bg-slate-50 rounded-full w-20 h-20 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 p-2 border-4 border-lime-600"
            title="EZY Assistant - Drag to reposition"
          >
            <img 
              src="/customer-assets/Ezy_Mobility_Logo_App.png" 
              alt="EZY" 
              className="w-full h-full object-cover rounded-full"
            />
          </Button>
          {isDragging && (
            <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-lime-600 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap">
              Drag to reposition
            </div>
          )}
        </div>
      )}

      {/* AI Chatbot */}
      <AIChatbot 
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        isMinimized={chatMinimized}
        onToggleMinimize={() => setChatMinimized(!chatMinimized)}
      />
    </div>
  );
};

export default Layout;
